// src/pages/admin/AdminDiagnostics.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./admin.css";

export default function AdminDiagnostics() {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastCheck, setLastCheck] = useState(null);

  const token = localStorage.getItem("token");
  const adminRole = localStorage.getItem("adminRole");

  // Redirect if not authenticated as admin
  useEffect(() => {
    if (!token || adminRole !== "admin") {
      navigate("/admin/login");
      return;
    }
  }, [token, adminRole, navigate]);

  // Check health on mount and set up auto-refresh
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/health`);
        const data = await res.json();
        setHealth(data);
        setLastCheck(new Date());
        setLoading(false);
      } catch (err) {
        console.error("Health check failed:", err);
        setHealth({
          status: "ERROR",
          error: err.message,
          timestamp: new Date().toISOString(),
          checks: { api: "FAILED", database: "UNKNOWN" }
        });
        setLoading(false);
      }
    };

    checkHealth();

    // Auto-refresh every 30 seconds if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(checkHealth, 30000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  if (!token || adminRole !== "admin") {
    return null;
  }

  return (
    <div style={{ padding: "30px", maxWidth: "1200px", margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
        <h1>🏥 System Diagnostics</h1>
        <button
          onClick={() => navigate("/admin/dashboard")}
          style={{
            padding: "10px 20px",
            backgroundColor: "#667eea",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer"
          }}
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Controls */}
      <div style={{ marginBottom: "20px", display: "flex", gap: "10px" }}>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "10px 16px",
            backgroundColor: "#28a745",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer"
          }}
        >
          🔄 Refresh Now
        </button>
        <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          <span>Auto-refresh every 30s</span>
        </label>
      </div>

      {/* Last Check Time */}
      {lastCheck && (
        <div
          style={{
            marginBottom: "20px",
            padding: "10px",
            backgroundColor: "#f0f0f0",
            borderRadius: "6px",
            fontSize: "13px",
            color: "#666"
          }}
        >
          Last checked: {lastCheck.toLocaleTimeString()}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          Loading health information...
        </div>
      ) : !health ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#c33" }}>
          Failed to load health information
        </div>
      ) : (
        <>
          {/* Overall Status */}
          <div
            style={{
              marginBottom: "20px",
              padding: "20px",
              borderRadius: "8px",
              backgroundColor:
                health.status === "OK"
                  ? "#d4edda"
                  : health.status === "DEGRADED"
                  ? "#fff3cd"
                  : "#f8d7da",
              border: `2px solid ${
                health.status === "OK"
                  ? "#28a745"
                  : health.status === "DEGRADED"
                  ? "#ffc107"
                  : "#dc3545"
              }`,
              color:
                health.status === "OK"
                  ? "#155724"
                  : health.status === "DEGRADED"
                  ? "#856404"
                  : "#721c24"
            }}
          >
            <h2 style={{ margin: "0 0 10px 0" }}>
              {health.status === "OK" && "✅ System Healthy"}
              {health.status === "DEGRADED" && "⚠️ Degraded Mode"}
              {health.status === "ERROR" && "❌ System Error"}
            </h2>
            <p style={{ margin: "0" }}>
              Status: <strong>{health.status}</strong> | Timestamp: {health.timestamp}
            </p>
          </div>

          {/* API Status */}
          <div style={{ marginBottom: "20px" }}>
            <h3 style={{ borderBottom: "2px solid #667eea", paddingBottom: "10px" }}>
              API Service
            </h3>
            <div
              style={{
                padding: "15px",
                backgroundColor:
                  health.checks.api === "OK" ? "#d4edda" : "#f8d7da",
                borderRadius: "6px",
                marginTop: "10px"
              }}
            >
              <strong>Status:</strong>{" "}
              {health.checks.api === "OK" ? "✅ Running" : "❌ Failed"}
            </div>
          </div>

          {/* Database Status */}
          <div style={{ marginBottom: "20px" }}>
            <h3 style={{ borderBottom: "2px solid #667eea", paddingBottom: "10px" }}>
              Database (Azure Cosmos DB)
            </h3>

            {health.checks.cosmosDiagnostics && (
              <div style={{ marginTop: "10px" }}>
                {/* Connection Status */}
                <div
                  style={{
                    padding: "15px",
                    backgroundColor:
                      health.checks.cosmosDiagnostics.connectionStatus === "CONNECTED"
                        ? "#d4edda"
                        : "#f8d7da",
                    borderRadius: "6px",
                    marginBottom: "15px"
                  }}
                >
                  <strong>Connection:</strong>{" "}
                  {health.checks.cosmosDiagnostics.connectionStatus === "CONNECTED"
                    ? "✅ Connected"
                    : "❌ Failed"}
                  <br />
                  <small style={{ color: "#666" }}>
                    Endpoint: {health.checks.cosmosDiagnostics.endpoint || "N/A"}
                  </small>
                </div>

                {/* Database Status */}
                <div
                  style={{
                    padding: "15px",
                    backgroundColor:
                      health.checks.cosmosDiagnostics.databaseStatus === "OK"
                        ? "#d4edda"
                        : "#f8d7da",
                    borderRadius: "6px",
                    marginBottom: "15px"
                  }}
                >
                  <strong>Database:</strong>{" "}
                  {health.checks.cosmosDiagnostics.databaseStatus === "OK"
                    ? "✅ OK"
                    : "❌ Failed"}
                  {health.checks.cosmosDiagnostics.diagnosticInfo && (
                    <div style={{ marginTop: "10px", fontSize: "13px" }}>
                      {health.checks.cosmosDiagnostics.diagnosticInfo.databaseName && (
                        <>
                          <strong>Database:</strong>{" "}
                          {health.checks.cosmosDiagnostics.diagnosticInfo.databaseName}
                          <br />
                        </>
                      )}
                      {health.checks.cosmosDiagnostics.diagnosticInfo.responseTime && (
                        <>
                          <strong>Response Time:</strong>{" "}
                          {health.checks.cosmosDiagnostics.diagnosticInfo.responseTime}
                          <br />
                        </>
                      )}
                      {health.checks.cosmosDiagnostics.diagnosticInfo.error && (
                        <>
                          <strong>Error:</strong>{" "}
                          {health.checks.cosmosDiagnostics.diagnosticInfo.error}
                          <br />
                        </>
                      )}
                      {health.checks.cosmosDiagnostics.diagnosticInfo.issue && (
                        <>
                          <strong>Issue:</strong>{" "}
                          {health.checks.cosmosDiagnostics.diagnosticInfo.issue}
                          <br />
                        </>
                      )}
                      {health.checks.cosmosDiagnostics.diagnosticInfo.possibleCause && (
                        <>
                          <strong>Possible Causes:</strong>
                          <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
                            {health.checks.cosmosDiagnostics.diagnosticInfo.possibleCause.map(
                              (cause, idx) => (
                                <li key={idx}>{cause}</li>
                              )
                            )}
                          </ul>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Containers Status */}
                {health.checks.cosmosDiagnostics.containers &&
                  Object.keys(health.checks.cosmosDiagnostics.containers).length > 0 && (
                    <div style={{ marginTop: "15px" }}>
                      <h4>Containers:</h4>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(250px, 1fr))",
                          gap: "10px"
                        }}
                      >
                        {Object.entries(
                          health.checks.cosmosDiagnostics.containers
                        ).map(([name, info]) => (
                          <div
                            key={name}
                            style={{
                              padding: "12px",
                              backgroundColor:
                                info.status === "OK" ? "#d4edda" : "#f8d7da",
                              borderRadius: "6px",
                              fontSize: "13px"
                            }}
                          >
                            <strong>
                              {info.status === "OK" ? "✅" : "❌"} {name}
                            </strong>
                            {info.responseTime && (
                              <div style={{ marginTop: "5px", color: "#666" }}>
                                Response: {info.responseTime}
                              </div>
                            )}
                            {info.error && (
                              <div
                                style={{
                                  marginTop: "5px",
                                  color: "#c33",
                                  fontSize: "11px"
                                }}
                              >
                                {info.error}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            )}
          </div>

          {/* Raw JSON (for developers) */}
          <div style={{ marginTop: "30px" }}>
            <details
              style={{
                padding: "15px",
                backgroundColor: "#f5f5f5",
                borderRadius: "6px",
                cursor: "pointer"
              }}
            >
              <summary style={{ fontWeight: "bold", cursor: "pointer" }}>
                📋 Raw Health Check Response (Developer Info)
              </summary>
              <pre
                style={{
                  marginTop: "10px",
                  padding: "10px",
                  backgroundColor: "#fff",
                  borderRadius: "4px",
                  overflow: "auto",
                  fontSize: "12px"
                }}
              >
                {JSON.stringify(health, null, 2)}
              </pre>
            </details>
          </div>
        </>
      )}
    </div>
  );
}